import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPlatform, getAppVersion } from "@/lib/platform";
import { compareVersions, type LockedStylesFlag } from "@/lib/feature-flag-eval";

/**
 * Feature-visibility hook for the "Room Refresh" feature flag.
 *
 * Unlike `useFeatureFlagEnforced` (which models *restrictions* and therefore
 * lets paid users bypass), Room Refresh is a *feature launch* flag: visibility
 * follows the same audience knobs (global / admins-only / beta users / mobile
 * version gates / admin bypass) but is NOT auto-enabled for every paid user.
 * A paid user only sees Room Refresh when the flag targets them, exactly like
 * a free user.
 *
 * Visibility is cached in localStorage so the UI (web tile, mobile tab bar)
 * renders immediately on repeat visits instead of popping in after the flag
 * network calls resolve. The server still enforces the flag on generation.
 */
export interface UseRoomRefreshFlagResult {
  loading: boolean;
  visible: boolean;
  isAdmin: boolean;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = { flag: null as LockedStylesFlag | null, fetchedAt: 0 };

const VISIBILITY_LS_KEY = "rr_visible";

interface VisibilityCacheEntry {
  uid: string;
  visible: boolean;
}

const readVisibilityCache = (): VisibilityCacheEntry | null => {
  try {
    const raw = localStorage.getItem(VISIBILITY_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VisibilityCacheEntry;
    if (typeof parsed?.uid === "string" && typeof parsed?.visible === "boolean") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

const writeVisibilityCache = (uid: string, visible: boolean) => {
  try {
    localStorage.setItem(VISIBILITY_LS_KEY, JSON.stringify({ uid, visible }));
  } catch {
    // storage unavailable — non-fatal
  }
};

const clearVisibilityCache = () => {
  try {
    localStorage.removeItem(VISIBILITY_LS_KEY);
  } catch {
    // ignore
  }
};

const fetchFlag = async (): Promise<LockedStylesFlag | null> => {
  if (cache.flag && Date.now() - cache.fetchedAt < CACHE_TTL_MS) return cache.flag;
  const { data } = await supabase
    .from("feature_flags")
    .select(
      "enabled_global,enabled_for_admins,enabled_user_ids,min_enforce_android_version,min_enforce_ios_version,admin_bypass"
    )
    .eq("key", "room_refresh")
    .maybeSingle();
  if (!data) return null;
  const flag: LockedStylesFlag = {
    enabled_global: !!data.enabled_global,
    enabled_for_admins: !!data.enabled_for_admins,
    enabled_user_ids: (data.enabled_user_ids as string[] | null) ?? [],
    min_enforce_android_version: data.min_enforce_android_version ?? null,
    min_enforce_ios_version: data.min_enforce_ios_version ?? null,
    admin_bypass: (data as { admin_bypass?: boolean | null }).admin_bypass ?? true,
  };
  cache.flag = flag;
  cache.fetchedAt = Date.now();
  return flag;
};

export const invalidateRoomRefreshCache = () => {
  cache.flag = null;
  cache.fetchedAt = 0;
  clearVisibilityCache();
};

export const useRoomRefreshFlag = (): UseRoomRefreshFlagResult => {
  // Optimistic init: if we have a cached visibility result, paint the UI
  // immediately and skip the loading state while we re-validate in the
  // background. First-ever visits (no cache) still start in loading state.
  const [loading, setLoading] = useState(() => readVisibilityCache() === null);
  const [visible, setVisible] = useState(() => readVisibilityCache()?.visible ?? false);
  const [isAdmin, setIsAdmin] = useState(false);

  const evaluate = useCallback(async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user ?? null;
      if (!user) {
        clearVisibilityCache();
        setIsAdmin(false);
        setVisible(false);
        return;
      }

      const [flag, profileRes, roleRes, appVersion] = await Promise.all([
        fetchFlag(),
        supabase
          .from("profiles")
          .select("subscription_tier")
          .eq("id", user.id)
          .maybeSingle(),
        supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
        getAppVersion(),
      ]);

      const admin = !!roleRes.data;
      setIsAdmin(admin);

      let result = false;

      if (flag) {
        // Admin bypass — admins see the feature unless explicitly disabled.
        if (admin && flag.admin_bypass !== false) {
          result = true;
        } else {
          // Mobile version gate — hide on older binaries that lack the UI.
          const platform = getPlatform();
          if (platform === "android" || platform === "ios") {
            const gate =
              platform === "android"
                ? flag.min_enforce_android_version
                : flag.min_enforce_ios_version;
            if (gate) {
              if (!appVersion || compareVersions(appVersion, gate) < 0) {
                setVisible(false);
                writeVisibilityCache(user.id, false);
                return;
              }
            }
          }

          // Audience checks (no paid-user bypass — this is a feature launch).
          result =
            flag.enabled_global ||
            (flag.enabled_for_admins && admin) ||
            (!!flag.enabled_user_ids && flag.enabled_user_ids.includes(user.id));
        }
      }

      setVisible(result);
      writeVisibilityCache(user.id, result);
    } catch (e) {
      console.warn("useRoomRefreshFlag eval failed:", e);
      // Keep the cached/optimistic visibility on transient failures rather
      // than hiding a feature the user previously had.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    evaluate();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        clearVisibilityCache();
        setVisible(false);
        setIsAdmin(false);
        setLoading(true);
        return;
      }
      evaluate();
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, [evaluate]);

  return { loading, visible, isAdmin };
};
